import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  IconButton,
  Stack,
  Typography
} from "@mui/material";

import ChevronDownIcon from "assets/icons/ChevronDown";
import CopyIcon from "assets/icons/Copy";
import DownloadIcon from "assets/icons/Download";

import {getFolderAnchor, splitDirectionFileEntries} from "../helpers";
import type {FileTreeEntry, FileTreeFolder} from "../types";
import DirectionFilesAccordion from "./DirectionFilesAccordion";
import MaterialFileItem from "./MaterialFileItem";

type FileTreeFolderItemProps = {
  folder: FileTreeFolder;
  level: number;
  onCopyFolderLink: (path: string) => void;
  onExportFolder: (path: string) => void;
  isExporting: boolean;
  activeExportPath: string | null;
}

const renderFileTreeEntry = (
  entry: FileTreeEntry,
  level = 0,
  onCopyFolderLink: (path: string) => void,
  onExportFolder: (path: string) => void,
  activeExportPath: string | null,
) => {
  if (entry.type === 'file') {
    return <MaterialFileItem key={entry.file.id} item={entry.file}/>;
  }

  return (
    <FileTreeFolderItem
      key={`folder-${level}-${entry.name}`}
      folder={entry}
      level={level}
      onCopyFolderLink={onCopyFolderLink}
      onExportFolder={onExportFolder}
      isExporting={activeExportPath === entry.path}
      activeExportPath={activeExportPath}
    />
  );
};

const FileTreeFolderItem = ({
                              folder,
                              level,
                              onCopyFolderLink,
                              onExportFolder,
                              isExporting,
                              activeExportPath,
                            }: FileTreeFolderItemProps) => {
  const {directionFiles, otherEntries} = level === 1
    ? splitDirectionFileEntries(folder.children)
    : {directionFiles: [], otherEntries: folder.children};

  return (
    <Accordion
      id={getFolderAnchor(folder.path)}
      defaultExpanded
      disableGutters
      sx={{
        p: 0,
        boxShadow: 'none',
        borderRadius: '8px !important',
        backgroundColor: level === 0 ? 'white' : 'background.paper',
        overflow: 'hidden',
        '&.Mui-expanded': {
          backgroundColor: level === 0 ? 'white' : 'background.paper',
        },
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={isExporting ? <CircularProgress size={20}/> : <ChevronDownIcon sx={{width: 20, height: 20}}/>}
        sx={{
          px: level === 0 ? 0 : 2.5,
          py: 2,
          '& .MuiAccordionSummary-content': {
            m: 0,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant={level === 0 ? 'h5' : 'h6'}>
            {folder.name}
          </Typography>
          <IconButton
            aria-label={`Скопировать ссылку на папку ${folder.name}`}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onCopyFolderLink(folder.path);
            }}
            sx={{p: 0.5}}
          >
            <CopyIcon sx={{width: 20, height: 20}}/>
          </IconButton>
          <IconButton
            aria-label={`Скачать архив папки ${folder.name}`}
            size="small"
            disabled={Boolean(activeExportPath)}
            onClick={(event) => {
              event.stopPropagation();
              onExportFolder(folder.path);
            }}
            sx={{p: 0.5}}
          >
            <DownloadIcon sx={{width: 20, height: 20}}/>
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{px: level === 0 ? 0 : 2.5, pt: 0, pb: 2.5}}>
        <Stack spacing={2}>
          {otherEntries.map((entry) => renderFileTreeEntry(
            entry,
            level + 1,
            onCopyFolderLink,
            onExportFolder,
            activeExportPath,
          ))}
          {directionFiles.length > 0 && (
            <DirectionFilesAccordion files={directionFiles}/>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default FileTreeFolderItem;
